import { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useForm } from "react-hook-form";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../utils/constant";
import { toast } from "react-toastify";

import { Accordion, Row, Col, Form, Spinner } from "react-bootstrap";
import { Rating } from "react-simple-star-rating";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTypeConfig } from "../../utils/useTypeConfig";

const MAX_IMAGES = 3;

function CloseTicketForm({
  show,
  handleClose,
  ticket,
  fetchTickets,
  onClose,
  onCloseTicketSaved,
}) {
  const [loading, setLoading] = useState(false);
  const { labels } = useTypeConfig();
  const inputRefs = useRef([]);

  const [expanded, setExpanded] = useState("0");
  const [formData, setFormData] = useState({
    images: [],
    customerName:
      ticket?.subCustomerDetails?.customer_name ||
      ticket?.customerDetails?.customer_name ||
      "",
    customerNumber: ticket?.phone_number || "",
    customerRating: 1,
    technicianName: ticket?.assignTo?.first_name || "",
    technicianNumber: ticket?.assignTo?.phone_number || "",
  });

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      customerName:
        ticket?.subCustomerDetails?.customer_name ||
        ticket?.customerDetails?.customer_name ||
        "",
      customerNumber: ticket?.phone_number || "",
      technicianName: ticket?.assignTo?.first_name || "",
      technicianNumber: ticket?.assignTo?.phone_number || "",
    }));
  }, [ticket]);

  const [fsr_data, setfsr_data] = useState(ticket.fsr_data);
  const [showCheckpoints, setShowCheckpoints] = useState(false);

  useEffect(() => {
    if (fsr_data) {
      const initialFormData = {};
      const categoryData = fsr_data.categoryName;
      categoryData?.checkpoints?.forEach((checkpoint) => {
        initialFormData[checkpoint.id] = {
          checkpointId: checkpoint.id,
          statusValue:
            checkpoint.displayType === "calendar"
              ? { startDate: "", endDate: "" }
              : "",
          categoryId: categoryData.id,
        };
      });
      setFormData((prevData) => ({ ...prevData, ...initialFormData }));
    }
  }, [fsr_data]);

  const handleStatusChange = (
    checkpointId,
    value,
    categoryId,
    field = null,
  ) => {
    setFormData((prevData) => {
      const existingCheckpointData = prevData[checkpointId] || {
        checkpointId,
        statusValue: field ? { startDate: "", endDate: "" } : "",
        categoryId,
      };

      return {
        ...prevData,
        [checkpointId]: {
          ...existingCheckpointData,
          statusValue: field
            ? { ...existingCheckpointData.statusValue, [field]: value }
            : value,
          categoryId,
        },
      };
    });
  };

  const renderCheckpoints = () => {
    const category = fsr_data?.categoryName;
    if (!category) return null;

    return (
      <div key={category.id} className="mb-4">
        <h5>{category.name}</h5>
        {category?.checkpoints?.map((checkpoint) => (
          <Row className="mb-3" key={checkpoint.id}>
            <Col xs={12} md={4}>
              <Form.Group controlId={`checkpoint-${checkpoint.id}`}>
                <Form.Label>{checkpoint.checkpoint_name}</Form.Label>
              </Form.Group>
            </Col>
            <Col xs={12} md={8}>
              <Form.Group controlId={`status-${checkpoint.id}`}>
                {checkpoint.displayType === "text" && (
                  <Form.Control
                    type="text"
                    value={formData[checkpoint.id]?.statusValue || ""}
                    onChange={(e) =>
                      handleStatusChange(
                        checkpoint.id,
                        e.target.value,
                        category.id,
                      )
                    }
                  />
                )}
                {checkpoint.displayType === "calendar" && (
                  <div>
                    <Row>
                      <Col>
                        <Form.Control
                          type="date"
                          value={
                            formData[checkpoint.id]?.statusValue?.startDate ||
                            ""
                          }
                          placeholder="Start Date"
                          onChange={(e) =>
                            handleStatusChange(
                              checkpoint.id,
                              e.target.value,
                              category.id,
                              "startDate",
                            )
                          }
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          type="date"
                          value={
                            formData[checkpoint.id]?.statusValue?.endDate || ""
                          }
                          placeholder="End Date"
                          onChange={(e) =>
                            handleStatusChange(
                              checkpoint.id,
                              e.target.value,
                              category.id,
                              "endDate",
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                )}
                {checkpoint.displayType === "dropdown" && (
                  <Form.Control
                    as="select"
                    value={formData[checkpoint.id]?.statusValue || ""}
                    onChange={(e) =>
                      handleStatusChange(
                        checkpoint.id,
                        e.target.value,
                        category.id,
                      )
                    }
                  >
                    <option value="">Select a status</option>
                    {checkpoint?.checkpointStatuses?.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Control>
                )}
              </Form.Group>
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const handleRatingChange = (rating) => {
    setFormData((prevData) => ({ ...prevData, customerRating: rating }));
  };

  const initializeImages = () => {
    const initialImages = Array.from({ length: MAX_IMAGES }, (_, index) => {
      const fieldName = `after_task_images_${index + 1}`;
      return {
        id: index + 1,
        file: ticket ? ticket[fieldName] : null,
        fieldName: fieldName,
      };
    });
    setFormData({
      ...formData,
      images: initialImages,
    });
  };

  useEffect(() => {
    if (ticket) {
      initializeImages();
      reset({
        status: ticket.status || "Completed",
        after_note: ticket.after_note || "",
      });
    } else {
      reset({});

      setFormData({ images: [] });
    }
  }, [show, ticket, reset]);

  const handleImageChange = (e, index) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
      const maxSize = 5 * 1024 * 1024;

      if (!validImageTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload a JPEG or PNG image.");
        return;
      }

      if (file.size > maxSize) {
        toast.error(
          "File is too large. Please upload an image smaller than 5MB.",
        );
        return;
      }

      const updatedImages = formData.images.map((img, idx) =>
        idx === index ? { ...img, file } : img,
      );
      setFormData({ ...formData, images: updatedImages });
    }
  };

  const handleImageClick = (id) => {
    inputRefs.current[id - 1].click();
  };

  const validateCheckpoints = () => {
    const newErrors = {};

    const category = fsr_data?.categoryName;
    if (category?.checkpoints) {
      category.checkpoints.forEach((checkpoint) => {
        const checkpointData = formData[checkpoint.id];
        if (
          !checkpointData ||
          (checkpoint.displayType === "calendar" &&
            (!checkpointData.statusValue?.startDate ||
              !checkpointData.statusValue?.endDate)) ||
          !checkpointData.statusValue
        ) {
          newErrors[checkpoint.id] =
            `${checkpoint.checkpoint_name} is required`;
        }
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleGenerateOtp = async () => {
    if (!ticket?.id || !formData.customerNumber) {
      toast.error("Invalid Ticket ID or Customer Number");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/api/ticket/${ticket.id}/${formData.customerNumber}/otp/`,
      );

      if (response.data.message === "OTP Code Sent") {
        toast.success("OTP Code Sent");
        setOtpSent(true);
      }
    } catch (error) {
      let errorMessage = "Failed to send OTP. Try again.";

      if (error.response?.data?.error) {
        try {
          const parsed = JSON.parse(error.response.data.error);
          errorMessage = parsed[0] || errorMessage;
        } catch {
          errorMessage = error.response.data.error;
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!ticket?.id || !otp) {
      toast.error("Please enter OTP");
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/api/ticket/${ticket.id}/${otp}/`,
      );
      if (response.status === 200) {
        toast.success("OTP Verified Successfully");
        setOtpVerified(true);
      } else {
        toast.error("Invalid OTP");
      }
    } catch (error) {
      toast.error("Error verifying OTP");
    }
  };

  const handleFormSubmit = async (data) => {
    if (!otpVerified) {
      toast.error("Please verify OTP before submitting");
      return;
    }

    if (!validateCheckpoints()) {
      toast.error("Fill all checkpoints before submitting.");
      return;
    }

    if (
      formData.images.length === 0 ||
      !formData.images.some((img) => img.file)
    ) {
      toast.error("At least one image must be uploaded.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("status", "Completed");
    formDataToSend.append("after_note", data.after_note || "");
    formDataToSend.append("technical_note", data.technical_note || "");
    formDataToSend.append("workmode", data.workmode || "");

    formDataToSend.append("cust_name", formData.customerName || "");
    formDataToSend.append("cust_number", formData.customerNumber || "");
    formDataToSend.append("cust_rating", formData.customerRating || "1");
    formDataToSend.append("technician_name", formData.technicianName || "");
    formDataToSend.append("technician_number", formData.technicianNumber || "");

    const ticketCheckpoints = Object.keys(formData)
      .filter((key) => !isNaN(parseInt(key)))
      ?.map((key) => {
        const { checkpointId, statusValue, categoryId } = formData[key];
        const status =
          typeof statusValue === "object"
            ? { startDate: statusValue.startDate, endDate: statusValue.endDate }
            : statusValue;
        return {
          checkpoint: checkpointId,
          status,
          category: categoryId,
        };
      });

    formDataToSend.append(
      "ticket_checkpoints",
      JSON.stringify(ticketCheckpoints),
    );

    formData.images.forEach((image, index) => {
      if (image.file) {
        formDataToSend.append(`after_task_images_${index + 1}`, image.file);
      } else if (image.url) {
        formDataToSend.append(`after_task_images_${index + 1}`, image.url);
      }
    });

    const apiUrl = `${BASE_URL}/api/tickets/${ticket.id}/`;
    const apiMethod = axios.put;

    try {
      setLoading(true);
      const response = await apiMethod(apiUrl, formDataToSend, {
        headers: { ...HEADERS, "Content-Type": "multipart/form-data" },
      });
      if (response) {
        handleClose();
        onClose();
        onCloseTicketSaved();
        fetchTickets();
        toast.success("Ticket Status Updated Successfully");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage);

        toast.error("Error Please Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="">
          <Modal.Title className="w-100 ">Completed Form</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "80vh",
            overflowY: "auto",
            padding: "20px",
          }}
        >
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <>
              <div className="upload-photo-form mb-2">
                <p>
                  Please upload pictures after completing the task (At least one
                  image is required)
                </p>
                <Form>
                  <Row
                    className="justify-content-center gap-3"
                    style={{ flexWrap: "wrap", overflow: "hidden" }}
                  >
                    {formData?.images?.map((field, index) => (
                      <Col
                        xs={6}
                        md={4}
                        lg={3}
                        key={field.id}
                        className="d-flex justify-content-center align-items-center"
                      >
                        <div
                          className="border rounded p-2 position-relative d-flex flex-column align-items-center"
                          style={{
                            height: "140px",
                            width: "100%",
                            cursor: "pointer",
                            backgroundColor: "#f8f9fa",
                            overflow: "hidden",
                          }}
                          onClick={() => handleImageClick(field.id)}
                        >
                          <Form.Group controlId={`formFile${field.id}`}>
                            <Form.Label
                              className="d-flex flex-column align-items-center justify-content-center"
                              style={{ cursor: "pointer", height: "100%" }}
                            >
                              {field.file ? (
                                <img
                                  src={
                                    field.file instanceof File
                                      ? URL.createObjectURL(field.file)
                                      : field.file
                                  }
                                  alt="Upload"
                                  style={{
                                    maxHeight: "100%",
                                    maxWidth: "100%",
                                    objectFit: "contain",
                                    borderRadius: "4px",
                                  }}
                                />
                              ) : (
                                <div className="text-center">
                                  <FaCloudUploadAlt size={50} />
                                </div>
                              )}
                              <input
                                type="file"
                                className="form-control-file d-none"
                                accept="image/jpeg, image/png, image/jpg"
                                onChange={(e) => handleImageChange(e, index)}
                                ref={(el) => (inputRefs.current[index] = el)}
                              />
                            </Form.Label>
                          </Form.Group>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Form>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="after_note" className="form-label">
                    Note
                  </label>
                  <textarea
                    id="after_note"
                    className="form-control"
                    {...register("after_note")}
                  />
                </div>
              </div>

              <h6>Work Mode*</h6>
              <Form.Group controlId="workMode" className="mb-3">
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    label="Installation"
                    name="workMode"
                    className="me-2"
                    id="installation"
                    {...register("workmode", {
                      required: "Work mode is required",
                    })}
                    value="Installation"
                  />
                  <Form.Check
                    type="radio"
                    label="Repair"
                    name="workMode"
                    className="me-2"
                    id="repair"
                    {...register("workmode", {
                      required: "Work mode is required",
                    })}
                    value="Repair"
                  />
                  <Form.Check
                    type="radio"
                    label="Service"
                    name="workMode"
                    id="service"
                    {...register("workmode", {
                      required: "Work mode is required",
                    })}
                    value="Service"
                  />
                </div>
                {errors.workmode && (
                  <div className="text-danger">{errors.workmode.message}</div>
                )}
              </Form.Group>

              <h5 className="mt-4">FSR: {fsr_data?.fsrName}</h5>
              <Button
                variant="primary"
                onClick={() => setShowCheckpoints(!showCheckpoints)}
                className="mb-3"
              >
                {showCheckpoints ? "Hide Checkpoints" : "Show All Checkpoints"}
              </Button>
              {showCheckpoints && <div>{renderCheckpoints()}</div>}

              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="technical_note" className="form-label">
                    Technical Notes
                  </label>
                  <textarea
                    id="technical_note"
                    rows={3}
                    className="form-control"
                    {...register("technical_note")}
                  />
                </div>
              </div>

              <Accordion
                activeKey={expanded}
                onSelect={(key) => setExpanded(key)}
              >
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Other Information</Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="customerName">
                          <Form.Label>{labels.customerName}*</Form.Label>
                          <Form.Control
                            type="text"
                            name="customerName"
                            {...register("customerName", {
                              required: "Name is required",
                            })}
                            value={formData.customerName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                customerName: e.target.value,
                              })
                            }
                          />
                          {errors.customerName && (
                            <div className="text-danger">
                              {errors.customerName.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group controlId="customerNumber" className="mt-2">
                          <Form.Label>Customer Number*</Form.Label>
                          <Form.Control
                            type="text"
                            name="customerNumber"
                            {...register("customerNumber", {
                              required: "Customer number is required",
                              validate: (value) =>
                                /^\d{10}$/.test(value) ||
                                "Enter a valid 10-digit number",
                            })}
                            value={formData.customerNumber}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(
                                /\D/g,
                                "",
                              );
                              setFormData({
                                ...formData,
                                customerNumber: numericValue,
                              });
                            }}
                          />
                          {errors.customerNumber && (
                            <div className="text-danger">
                              {errors.customerNumber.message}
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group
                          controlId="customerRating"
                          className="mt-2 mb-4"
                        >
                          <Form.Label>Customer Rating</Form.Label>
                          <Rating
                            onClick={handleRatingChange}
                            ratingValue={formData.customerRating}
                            initialValue={formData.customerRating}
                          />
                          {errors.customerRating && (
                            <div className="text-danger">
                              {errors.customerRating.message}
                            </div>
                          )}
                        </Form.Group>

                        <div classname="row mb-3">
                          <Row className="mb-3 mt-3">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Control
                                  type="text"
                                  value={otp}
                                  onChange={(e) => setOtp(e.target.value)}
                                  disabled={!otpSent}
                                  placeholder="Enter OTP"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-end">
                              {!otpSent ? (
                                <Button
                                  variant="primary"
                                  onClick={handleGenerateOtp}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    "Generate OTP"
                                  )}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="success"
                                    onClick={handleVerifyOtp}
                                    disabled={otpVerified || loading}
                                    className="me-2"
                                  >
                                    {otpVerified ? "✓ Verified" : "Verify"}
                                  </Button>
                                  {!otpVerified && (
                                    <Button
                                      variant="warning"
                                      onClick={handleGenerateOtp}
                                      disabled={loading}
                                    >
                                      {loading ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        "Regenerate"
                                      )}
                                    </Button>
                                  )}
                                </>
                              )}
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="technicianName">
                          <Form.Label>Technician Name*</Form.Label>
                          <Form.Control
                            type="text"
                            name="technicianName"
                            value={formData.technicianName}
                            disabled
                          />
                        </Form.Group>

                        <Form.Group
                          controlId="technicianNumber"
                          className="mt-2"
                        >
                          <Form.Label>Technician Number*</Form.Label>
                          <Form.Control
                            type="text"
                            name="technicianNumber"
                            value={formData.technicianNumber}
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="ms-2"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CloseTicketForm;
